import { useMutation } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { useRetrievalProgressStore } from '@/stores/retrievalProgressStore';
import { useRetrievalProgressInitialStore } from '@/stores/retrievalProgressInitialStore';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

// ============================================================================
// Types
// ============================================================================

export interface CancelRetrievalInput {
  id: string;
}

export interface CancelRetrievalResponse {
  cancelMetadataRetrieval: {
    id: string;
    status: string;
  };
}

// ============================================================================
// GraphQL
// ============================================================================

const CANCEL_METADATA_RETRIEVAL_MUTATION = `
  mutation CancelRetrieval($id: ID!) {
    cancelMetadataRetrieval(id: $id) {
      id
      status
    }
  }
`;

// ============================================================================
// Hook
// ============================================================================

export function useCancelRetrieval() {
  const mutation = useMutation({
    mutationFn: async ({ id }: CancelRetrievalInput) => {
      const response = await graphqlRequest<CancelRetrievalResponse>(
        CANCEL_METADATA_RETRIEVAL_MUTATION,
        { id }
      );

      if (!response.cancelMetadataRetrieval) {
        throw new Error('Failed to cancel metadata retrieval');
      }

      return response.cancelMetadataRetrieval;
    },
    onMutate: () => {
      // Optimistically tear down SSE + clear progress state so the UI flips to
      // 'idle' the moment the user clicks Cancel, without waiting for the API.
      useRetrievalProgressStore.getState().disconnect();
      useRetrievalProgressStore.getState().reset();
      useRetrievalProgressInitialStore.getState().disconnect();
      useRetrievalProgressInitialStore.getState().reset();
      useAuthStoreV1.setState((s) =>
        s.selectedOrg?.latestMetadataRetrieval
          ? {
              selectedOrg: {
                ...s.selectedOrg,
                latestMetadataRetrieval: {
                  ...s.selectedOrg.latestMetadataRetrieval,
                  status: 'CANCELLED',
                },
              },
            }
          : {}
      );
    },
    onSuccess: (data) => {
      console.log('✅ Metadata retrieval cancelled:', data.id);
    },
    onError: (error) => {
      console.error('❌ Failed to cancel metadata retrieval:', error);
    },
  });

  return {
    cancelRetrieval: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
